import { currency, cx } from '../lib/format.js';

/**
 * VariantSelector — pick a variant along two independent axes.
 *
 * Variants are stored flat (one document per storage × colour combination), but
 * presenting twenty flat options would be unusable. So the list is projected into
 * two selectors, the way a manufacturer's own product page works.
 *
 * The important detail is that the axes are not independent in the data: not
 * every colour exists at every capacity. Selecting a storage therefore filters
 * the colours to the ones that actually exist at that capacity, and any colour
 * with no matching variant is rendered disabled rather than silently missing —
 * so the shopper can see the combination exists but is unavailable.
 */
export default function VariantSelector({ variants, selectedVariant, onSelect }) {
  if (!variants?.length) return null;

  const storages = [...new Set(variants.map((v) => v.storage).filter(Boolean))];
  const colors = [...new Set(variants.map((v) => v.color).filter(Boolean))];

  const hasStorageAxis = storages.length > 1;
  const hasColorAxis = colors.length > 1;

  /** Find the best variant matching a desired storage/colour pair. */
  const resolve = (storage, color) =>
    variants.find((v) => v.storage === storage && v.color === color) ||
    variants.find((v) => v.storage === storage) ||
    variants.find((v) => v.color === color);

  // Neither axis is meaningful (a single variant, or unlabelled ones): fall back
  // to a plain list of labels so the control still works.
  if (!hasStorageAxis && !hasColorAxis) {
    if (variants.length === 1) return null;
    return (
      <fieldset>
        <legend className="text-sm font-semibold text-ink-800">Variant</legend>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {variants.map((variant) => (
            <OptionButton
              key={variant.id}
              selected={selectedVariant?.id === variant.id}
              disabled={!variant.inStock}
              onClick={() => onSelect(variant)}
            >
              {variant.label}
            </OptionButton>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <div className="space-y-6">
      {hasStorageAxis && (
        <fieldset>
          <div className="flex items-baseline justify-between">
            <legend className="text-sm font-semibold text-ink-800">Storage</legend>
            <span className="text-xs text-ink-500">{storages.length} options</span>
          </div>

          <div className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {storages.map((storage) => {
              const target = resolve(storage, selectedVariant?.color);
              const isSelected = selectedVariant?.storage === storage;
              const cheapest = Math.min(
                ...variants.filter((v) => v.storage === storage).map((v) => v.price),
              );

              return (
                <button
                  key={storage}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={!target}
                  onClick={() => target && onSelect(target)}
                  className={cx(
                    'rounded-xl border px-3 py-2.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-40',
                    isSelected
                      ? 'border-brand-500 bg-brand-50/70 ring-1 ring-brand-500'
                      : 'border-ink-200 bg-white hover:border-brand-300',
                  )}
                >
                  <span className="block text-sm font-semibold text-ink-900">{storage}</span>
                  <span className="mt-0.5 block text-xs text-ink-500">from {currency(cheapest)}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {hasColorAxis && (
        <fieldset>
          <div className="flex items-baseline justify-between">
            <legend className="text-sm font-semibold text-ink-800">
              Colour
              {selectedVariant?.color && (
                <span className="ml-1.5 font-normal text-ink-500">— {selectedVariant.color}</span>
              )}
            </legend>
          </div>

          <div className="mt-2.5 flex flex-wrap gap-2.5">
            {colors.map((color) => {
              // Only offer colours that exist at the currently chosen capacity.
              const target = variants.find(
                (v) => v.color === color && (!selectedVariant?.storage || v.storage === selectedVariant.storage),
              );
              const isSelected = selectedVariant?.color === color;
              const swatch = variants.find((v) => v.color === color)?.colorHex;

              return (
                <button
                  key={color}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={target ? color : `${color} — not available in ${selectedVariant?.storage}`}
                  title={target ? color : `Not available in ${selectedVariant?.storage}`}
                  disabled={!target}
                  onClick={() => target && onSelect(target)}
                  className={cx(
                    'group relative grid size-11 place-items-center rounded-full transition-all',
                    'disabled:cursor-not-allowed disabled:opacity-30',
                    isSelected ? 'ring-2 ring-brand-500 ring-offset-2' : 'ring-1 ring-ink-200 hover:ring-brand-300',
                  )}
                >
                  <span
                    className="size-8 rounded-full border border-black/10"
                    style={{ backgroundColor: swatch || '#d4d4d8' }}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
    </div>
  );
}

function OptionButton({ selected, disabled, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'rounded-xl border px-3.5 py-2 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-40',
        selected ? 'border-brand-500 bg-brand-50/70 ring-1 ring-brand-500' : 'border-ink-200 bg-white hover:border-brand-300',
      )}
    >
      {children}
    </button>
  );
}
